import { createContext, useContext } from "react";
import type { Session } from "./auth";

/**
 * What the signed-in person may do, as the server resolved it.
 *
 * The permission strings and the page map both come from `/auth/session`, so
 * the front end carries no second copy of which permission gates which page. A
 * page gated server-side appears in the navigation without a UI change, and —
 * more importantly — the two halves cannot disagree about who may see what.
 *
 * This is PRESENTATION. Every route re-checks the same permission server-side,
 * because a hidden link stops nobody from typing a URL or calling the API. What
 * hiding buys is that people are not shown controls that will refuse them.
 */
export interface Permissions {
  /** Everything the holder may do, e.g. "documents:export". */
  granted: Set<string>;
  /** Route path -> the permission that gates it, from the server. */
  pages: Record<string, string>;
  can: (permission: string) => boolean;
  /** Whether a route may be navigated to. Unlisted paths are open. */
  canSee: (path: string) => boolean;
}

export function buildPermissions(session: Session | null): Permissions {
  const granted = new Set(session?.permissions ?? []);
  const pages = session?.pages ?? {};
  const can = (permission: string) => granted.has(permission);
  return {
    granted,
    pages,
    can,
    // A path with no entry in the map is not gated. Defaulting to "allowed"
    // rather than "denied" is deliberate: the server is what enforces, and a
    // UI that hides a page the API would happily serve is a bug nobody can
    // diagnose from the screen.
    canSee: (path: string) => {
      const needed = pages[path];
      return needed === undefined || granted.has(needed);
    },
  };
}

const PermissionContext = createContext<Permissions>(buildPermissions(null));

export const PermissionProvider = PermissionContext.Provider;

/**
 * The permission set, anywhere below the provider.
 *
 * A context rather than threading the session through every page: the controls
 * that need gating are buttons deep inside tables, and passing a prop down to
 * each one would mean every intermediate component grew an argument it does
 * not use.
 */
export function usePermissions(): Permissions {
  return useContext(PermissionContext);
}

/** Convenience for the common single-permission case. */
export function useCan(permission: string): boolean {
  return useContext(PermissionContext).can(permission);
}
